/********************************************************************************************************/
/*******************************   GH RECYCLE BIN TABLE DIRECTIVE   ***************************************/
/********************************************************************************************************/

/* Purpose-built table for displaying trashed items inside the Recycle Bin dialog.
 * Split out from the general-purpose <gh-table> (gh_elements/table/gh_table.js) because trashed items
 * need two things the shared table doesn't do:
 *  - gh-element's "value" must bind to an assignable path, not a function call, otherwise AngularJS
 *    throws $compile:nonassign the moment a calculator/formula field recomputes its value.
 *  - filtering must tell the shared filter utility these items are trashed on purpose, otherwise it
 *    silently excludes every item as soon as any filter is applied.
 */

require('./gh_recycle_bin_table.scss')

angular.module('ghRecycleBinTableModule', [])

.directive('ghRecycleBinTable', ['$compile', '$filter', 'PipeService', '$animate', function($compile, $filter, PipeService, $animate) {
  return {
    restrict: 'E',
    scope: {
      ghAppId: '@',
      ghItemId: '@',
      ghFieldData: '=', /* field model */
      callback: '&?'
    },

    controller: ['$scope', '$element', async function($scope, $element) {

      $scope.appId = $scope.ghFieldData && $scope.ghFieldData.data_model && $scope.ghFieldData.data_model.app_id || $scope.ghAppId;

      if (!$scope.appId || !$scope.ghFieldData) {
        return;
      }

      if (!$scope.ghFieldData.data_model.table_settings.sort_table_by) {
        angular.extend($scope.ghFieldData.data_model.table_settings, {
          sort_table_by: { field_id: '' }
        });
      }

      $scope.fieldsToView = [];
      $scope.Math = Math;

      var address = {
        app_id: $scope.appId
      };

      var filters = {
        filters_list: $scope.ghFieldData.data_model.filters_list || [],
        search: $scope.ghFieldData.data_model.search
      };

      let filterUpdate = function(event, data) {
        filters.filters_list = gudhub.mergeFilters($scope.ghFieldData.data_model.filters_list, data);
        $scope.itemsList.applyFilter();
      };

      let searchUpdate = function(event, data) {
        filters.search = data;
        $scope.itemsList.applyFilter();
      };

      gudhub.on('filter', { app_id: $scope.ghFieldData.data_model.app_id, field_id: $scope.ghFieldData.field_id }, filterUpdate);
      gudhub.on('search', { app_id: $scope.ghFieldData.data_model.app_id, field_id: $scope.ghFieldData.field_id }, searchUpdate);

      $scope.$on('$destroy', function() {
        gudhub.destroy('filter', { app_id: $scope.ghFieldData.data_model.app_id, field_id: $scope.ghFieldData.field_id }, filterUpdate);
        gudhub.destroy('search', { app_id: $scope.ghFieldData.data_model.app_id, field_id: $scope.ghFieldData.field_id }, searchUpdate);
      });

      //-- Precomputes a per-item, non-enumerable field_id -> field lookup so table cells can bind
      //-- gh-element's value to an assignable path (item.__fieldsById[id].field_value) instead of a
      //-- function call, which AngularJS can never write back to.
      //--
      //-- field_value is an accessor, not a plain property: reads always recompute fresh from
      //-- item.fields (never cached/stale), and writes are ignored. This matters because when
      //-- md-virtual-repeat recycles a row onto a trashed item, gh-element's own itemId-recycle
      //-- handling (core/gh_element/gh_element.js) calls gudhub.getFieldValue(), which only looks
      //-- items up in the app's *active* items store and finds nothing for trashed ones, so it
      //-- explicitly sets the field to null. With a plain writable property that null would land
      //-- permanently on the same object item.fields points to, blanking the field for good (even
      //-- scrolling back to it later). A no-op setter + always-fresh getter makes that write harmless,
      //-- matching how the original field_val(item, field_id) function behaved (fresh read, and its
      //-- write-back always silently failed instead of corrupting anything).
      function indexItemFields(item) {
        if (!item) return item;
        if (!Object.prototype.hasOwnProperty.call(item, '__fieldsById')) {
          Object.defineProperty(item, '__fieldsById', { value: {}, enumerable: false, configurable: true });
        }
        ($scope.fieldsToView || []).forEach((fieldModel) => {
          if (!item.__fieldsById[fieldModel.field_id]) {
            const fieldId = fieldModel.field_id;
            const wrapper = { field_id: fieldId };
            Object.defineProperty(wrapper, 'field_value', {
              get: function() {
                const field = item.fields && item.fields.find((f) => f.field_id == fieldId);
                return (field && field.field_value) || '';
              },
              set: function() {}
            });
            item.__fieldsById[fieldId] = wrapper;
          }
        });
        return item;
      }

      function indexItemsFields(items) {
        (items || []).forEach(indexItemFields);
        return items;
      }

      class Items {
        constructor(items) {
          this.sortByField = {
            id: $scope.ghFieldData.data_model.table_settings.sort_table_by.field_id || null,
            descending: Boolean(+$scope.ghFieldData.data_model.table_settings.items_order)
          };
          this.items = items || [];
          this.filtered = this.items;
        }

        async applyFilter() {
          let self = this;
          const newItems = await gudhub.util.getFilteredItems(this.items, filters.filters_list,
              $scope.ghFieldData.data_model.app_id,
              $scope.ghAppId,
              $scope.ghItemId,
              $scope.ghFieldData.data_model.field_groupe,
              $scope.ghFieldData.data_model.search,
              filters.search,
              true); /* -- every item here is trashed on purpose; don't let the filter utility hide them */

          self.filtered = indexItemsFields(newItems);
          self.sortItemsByField(self.getSortByField().id);
          $scope.$digest();
        }

        getItemAtIndex(index) {
          return this.filtered[index];
        }

        getLength() {
          return this.filtered.length;
        }

        getItems() {
          return this.filtered;
        }

        sortItemsByField(fieldId, isUserIneracted) {
          if (this.sortByField.id == fieldId) {
            if (isUserIneracted) {
              this.filtered.reverse();
              let sortBy = this.getSortByField();
              sortBy.descending = !sortBy.descending;
            } else {
              this.filtered = $filter('orderSort')(this.getItems(), this.getSortByField());
            }
          } else {
            this.setSortByField({ id: fieldId, descending: true });
            this.filtered = $filter('orderSort')(this.getItems(), this.getSortByField());
          }
        }

        getSortByField() {
          return this.sortByField;
        }

        setSortByField(obj) {
          return this.sortByField = obj;
        }
      }

      const initialItems = ($scope.ghFieldData.data_model.replace_items || []).map((item) => {
        item.showView = false;
        return item;
      });
      $scope.itemsList = new Items(initialItems);
      await $scope.itemsList.applyFilter();

      // -------------------------------- GET FIELD LIST ------------------------------------------
      let lastFieldList = [];
      function refreshFieldsToView() {
        const columnsToView = $scope.ghFieldData.data_model.table_settings.columns_to_view || [];
        const merged = gudhub.util.mergeFieldLists(columnsToView, lastFieldList);
        //-- mergeFieldLists treats an absent field as "not yet configured" and adds it back in
        //-- using that field's own default show-in-table setting, rather than "explicitly
        //-- excluded". Recycle Bin's columns_to_view is a fully curated allowlist (only
        //-- constructor === 'field' entries, see recycle_bin.js), so re-filter the merge
        //-- output down to just those field_ids to keep excluded fields (e.g. Calendar,
        //-- App Table) from being silently re-added.
        $scope.fieldsToView = merged.filter((field) => columnsToView.some((column) => column.field_id == field.field_id));
        indexItemsFields($scope.itemsList.getItems());
      }

      PipeService.on('gh_models_get gh_models_update', address, function modelsPipe(event, fieldList) {
        PipeService.destroy('gh_models_get', address, modelsPipe);
        lastFieldList = fieldList;
        refreshFieldsToView();
        $scope.itemsList.sortItemsByField($scope.itemsList.getSortByField().id || $scope.fieldsToView[0].field_id);
        $scope.$apply();
      }, $scope).emit('gh_models_get', {}, address);

      //-- Recompute visible columns when a column's `show` flag is toggled (e.g. via the
      //-- Recycle Bin dialog's column show/hide menu), without waiting for a fresh model pipe event.
      $scope.$watch(function() {
        return $scope.ghFieldData.data_model.table_settings.columns_to_view;
      }, function(newVal, oldVal) {
        if (newVal === oldVal || !lastFieldList.length) return;
        refreshFieldsToView();
      }, true);

      // Set class to sortable column
      $scope.columnClass = function(fieldId) {
        let sortByField = $scope.itemsList.getSortByField();
        return fieldId == sortByField.id ? ' sort_' + (sortByField.descending ? 'desc' : 'asc') : null;
      };

      // Set info for selected item
      $scope.isSelected = function(item) {
        if (!item) return;
        return gudhub.util.isItemSelected($scope.appId, item.item_id);
      };

      $scope.isAllSelected = function() {
        return $scope.itemsList.getItems().every(i => gudhub.util.isItemSelected($scope.appId, i.item_id));
      };

      $scope.selectedItems = [];
      // Set\delete selected item to selection items array
      $scope.selectItem = async function(event, item) {
        if ($scope.ghFieldData.data_model.table_settings.selected_item == '1') {
          event.preventDefault();
          event.stopPropagation();
          const isItemSelected = $scope.isSelected(item);
          if ($scope.selectedItems.includes(item.item_id) && isItemSelected) {
            $scope.selectedItems = $scope.selectedItems.filter(itemId => itemId != item.item_id);
          } else {
            $scope.selectedItems.push(item.item_id);
          }
          await gudhub.util.selectItems($scope.appId, [item]);
          $scope.$emit('select-table-item', $scope.selectedItems);
          $scope.$digest();
        }
      };

      let lastItemsCount, lastHeight;
      $scope.getTableHeightStyle = function() {
        if (lastItemsCount == $scope.itemsList.getItems().length) return lastHeight;
        lastItemsCount = $scope.itemsList.getItems().length;
        setTimeout(() => {
          $element.find('md-virtual-repeat-container').controller('mdVirtualRepeatContainer').updateSize();
        }, 0);
        lastHeight = $scope.itemsList.getItems().length ? (Math.max($scope.ghFieldData.data_model.table_settings.table_height, 5) > $scope.itemsList.getItems().length ? $scope.itemsList.getItems().length : $scope.ghFieldData.data_model.table_settings.table_height || 5) * 64 + 42 + 'px' : 'initial';
        return lastHeight;
      };

      $scope.selectAllFilteredItems = function($event) {
        $scope.activeCheckBtn = !$scope.activeCheckBtn;
        if ($scope.activeCheckBtn) {
          gudhub.util.clearSelectedItems($scope.appId);
        }
        $scope.itemsList.getItems().forEach(i => {
          $scope.selectItem($event, i);
        });
      };

      $scope.virtualRepeatId = $scope.$id;

    }],

    link: function(scope, element) {
      var template =
        '<div class="gh-table-container" style="height: {{getTableHeightStyle()}}">' +

        '   <div class="repeated-item header" style="display: flex;" ng-show="itemsList.getItems().length != 0">' +

        '       <div ng-class="{\'noActiveCheckBtn\' : !activeCheckBtn}" class="gh-table-check" ng-click="selectAllFilteredItems($event)"></div>' +

        '       <div style="cursor: pointer" ng-click="itemsList.sortItemsByField(field.field_id, true)" class="{{field.field_id != itemsList.getSortByField().id ? \'\' : \'active-sorting\'}}" ng-style="{width: field.width, minWidth: field.width}" ng-repeat="field in fieldsToView track by $index">' +
        '           <span unselectable="on" onselectstart="return false;">{{field.field_name}}</span>' +
        '           <div class="table_sort_icon {{columnClass(field.field_id)}}"></div>' +
        '       </div>' +
        '   </div>' +
        '   <div class="no-items" ng-if="itemsList.getItems().length == 0">' +
        '       <div class="icon" gh-icon="alert dfdfdf 56px normal"></div>' +
        '       <div><p>No items matching this criteria</p></div>' +
        '   </div>' +
        '   <md-virtual-repeat-container class="flex-1" id="vertical-container-{{virtualRepeatId}}">' +
        '       <div md-on-demand="true" md-virtual-repeat="item in itemsList">' +
        '           <div class="repeated-item">' +
        '             <div class="row-count">' +
        '               <span class="{{isSelected(item) ? \' selected\' : \'\'}}" gh-icon="checked 0893d2 16px normal" ng-click="selectItem($event, item)"></span>' +
        '               <p>{{$index+1}}</p>' +
        '             </div>' +
        '             <div ng-repeat="field_model in fieldsToView" gh-element app-id="{{appId}}" item-id="{{item.item_id}}" field-id="{{field_model.field_id}}" value="item.__fieldsById[field_model.field_id].field_value" ng-style="{width: field_model.width, minWidth: field_model.width}" elem-src="table"></div>' +
        '           </div>' +
        '       </div>' +
        '   </md-virtual-repeat-container>' +
        '</div>';

      /* Append to DOM */
      // Disable ngAnimate for this table's subtree: the nested ng-repeat (field_model in
      // fieldsToView) inside each md-virtual-repeat row animates its own inserts, but
      // md-virtual-repeat recycles/detaches blocks synchronously outside that animation's
      // async callback, so the animation can try to insertBefore a node whose parent has
      // already been detached mid-scroll, throwing. Rows don't need enter/leave animation.
      $animate.enabled(element, false);

      var el = angular.element(template);
      element.append($compile(el)(scope));
    }
  };
}]);
